import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useFarmerStore } from '@/stores/admin/farmer.js'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import FarmerDocumentService from '@/services/admin/FarmerDocumentService.js'
import i18n from '@/i18n/i18n.js'
import { build_query, errorMessage } from '@/utilities/helpers.js'
import { DOCUMENT_TYPE_GROUPS, FARMER_DOCUMENT_TYPE } from '@/constants/farmerDocumentType.js'

const { t } = i18n.global

export const allowedSorts = 'created_at'
export const allowedAppends = 'document_verification_activity,mime_type'

const checkDocumentExist = (documents, types) => {
  return documents.filter(doc => types.includes(doc.type))[0]
}

export const useFarmerDocumentStore = defineStore('adminFarmerDocument', () => {
  // #region - Pinia State
  const documents = ref([])
  const document = ref(null)
  const viewDocumentData = ref(null)
  const farmerDocuments = ref([])
  const applicationDocuments = ref([])
  const documentVersions = ref([])

  // #region - Pinia Getters
  const getDocumentById = computed(() => id => {
    return documents.value ? documents.value.find(doc => doc.id === id) : false
  })

  const notificationStore = useNotificationStore()

  // #region - Pinia Helpers
  const sendNotification = (type, message) => {
    notificationStore.add({ type, message })
  }

  const setFarmerDocuments = farmerDocs => {
    const docs = [...farmerDocs]

    const idCard = checkDocumentExist(docs, DOCUMENT_TYPE_GROUPS.ID_CARDS)

    if (!idCard) {
      docs.push({
        type: FARMER_DOCUMENT_TYPE.NEW_ID_CARD,
        status: 0
      })
    }

    const requiredRegistryCertificateTypes = [
      FARMER_DOCUMENT_TYPE.FARMER_REGISTRY_CERTIFICATE,
      FARMER_DOCUMENT_TYPE.BUSINESS_REGISTRATION_CERTIFICATE
    ]

    requiredRegistryCertificateTypes.forEach(type => {
      const exists = docs.some(doc => doc.type === type)

      if (exists === false) {
        docs.push({
          type,
          status: 0
        })
      }
    })

    DOCUMENT_TYPE_GROUPS.INDIVIDUAL_DOCUMENTS.forEach(type => {
      const existingType = docs.find(doc => doc.type === type)
      if (!existingType) {
        docs.push({
          type: type,
          status: 0
        })
      }
    })

    const appDocs = []

    DOCUMENT_TYPE_GROUPS.APPLICATION_DOCUMENTS.forEach(type => {
      const existingType = docs.find(doc => doc.type === type)
      if (!existingType) {
        appDocs.push({
          type: type,
          status: 0
        })
      } else {
        appDocs.push(existingType)
        docs.splice(docs.indexOf(existingType), 1)
      }
    })

    applicationDocuments.value = appDocs
    farmerDocuments.value = docs
  }

  // #region - Pinia Actions
  const sendFarmerVerificationDocument = async form => {
    try {
      const response = await FarmerDocumentService.sendFarmerVerificationDocument(form)
      documents.value.push(response.data)
      sendNotification(
        'success',
        t('store.shared.added', {
          element: t('store.admin.farmers.document')
        })
      )
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  const updateFarmerDocumentStatus = async params => {
    try {
      const response = await FarmerDocumentService.updateFarmerDocumentStatus(
        params.farmerId,
        params.documentId,
        params.data
      )
      documents.value.push(response.data)

      const farmerStore = useFarmerStore()
      farmerStore.refreshFarmer(params.farmerId)

      sendNotification('success', t('store.admin.farmers.update_farmer_document_status_success'))
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  const deleteFarmerDocument = async params => {
    try {
      const response = await FarmerDocumentService.deleteFarmerDocument(params.farmerId, params.documentId)
      documents.value = documents.value.filter(doc => doc.id !== response.data.id)

      const farmerStore = useFarmerStore()
      farmerStore.refreshFarmer(params.farmerId)

      sendNotification('success', t('store.admin.farmers.document_deleted_successfully'))
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  const getDocuments = async id => {
    try {
      let query = {}
      query = build_query(query, null, null, allowedSorts, allowedAppends)
      const response = await FarmerDocumentService.getDocuments(id, query)
      documents.value = response.data
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  const getDocument = async params => {
    try {
      const response = await FarmerDocumentService.getDocument(params.farmerId, params.documentId)
      document.value = response.data
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  const viewDocument = async documentId => {
    try {
      const response = await FarmerDocumentService.viewDocument(documentId)
      viewDocumentData.value = response
      return response
    } catch (err) {
      sendNotification('danger', t('store.admin.farmers.document_getting_for_view_error') + errorMessage(err))
      throw err
    }
  }

  const listDocuments = async farmerId => {
    try {
      const response = await FarmerDocumentService.listDocuments(farmerId)
      setFarmerDocuments(response.data)
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  const getDocumentVersions = async params => {
    try {
      const response = await FarmerDocumentService.documentVersions(params.farmerId, params.documentId)
      documentVersions.value = response.data
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  const updateDocumentStatus = async params => {
    try {
      const response = await FarmerDocumentService.updateDocumentStatus(params.farmerId, params.documentId, params.data)
      sendNotification('success', t('store.admin.farmers.update_farmer_document_status_success'))
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  const revertDocumentDecision = async params => {
    try {
      const response = await FarmerDocumentService.revertDocumentDecision(params.farmerId, params.documentId)
      sendNotification('success', t('store.admin.farmers.revert_document_decision_success'))
      return response.data
    } catch (err) {
      sendNotification('danger', errorMessage(err))
      throw err
    }
  }

  return {
    documents,
    document,
    viewDocumentData,
    farmerDocuments,
    applicationDocuments,
    documentVersions,
    getDocumentById,
    sendFarmerVerificationDocument,
    updateFarmerDocumentStatus,
    deleteFarmerDocument,
    getDocuments,
    getDocument,
    viewDocument,
    listDocuments,
    getDocumentVersions,
    updateDocumentStatus,
    revertDocumentDecision
  }
})
